/**
 * Pad printing (Tampon Baskı / Pad Printing / Tampondruck): page copy + SEO.
 * Every string is VERBATIM from the pre-redesign TamponBaski.jsx,
 * en/PadPrinting.jsx and de/Tampondruck.jsx. The `seo` blocks reproduce the
 * exact head writes each page made (only TR carried keywords, Twitter tags and
 * a Service JSON-LD; EN carried OG tags; DE only title/description). The
 * canonical every page also wrote is CanonicalManager's now.
 * Object key order inside `schema` is load-bearing: JSON.stringify output must
 * stay byte-identical.
 */

const pad = {
  tr: {
    seo: {
      title: 'Tampon Baskı Hizmetleri | Bursa Tampon Baskı Yapan Firma - Ankaplastik',
      description:
        'Bursa tampon baskı hizmetleri. 3D yüzeylere mükemmel uyum sağlayan pad printing teknolojisi. Otomotiv trim parçaları, çok renkli baskı, mikron hassasiyeti. 25 yıllık deneyim, ISO 9001 sertifikalı.',
      keywords:
        'tampon baskı, bursa tampon baskı, tampon baskı yapan firmalar, pad printing, 3D baskı, otomotiv baskı, endüstriyel baskı Bursa',
      og: {
        'og:title': 'Tampon Baskı Hizmetleri | Ankaplastik Bursa',
        'og:description':
          "Bursa'da profesyonel tampon baskı (pad printing) hizmetleri. Otomotiv yan sanayi için hassas baskı çözümleri.",
        'og:type': 'website',
        'og:url': 'https://ankaplastik.com/tampon-baski',
        'og:image': 'https://ankaplastik.com/og-tampon-baski.jpg',
        'og:image:width': '1200',
        'og:image:height': '630',
        'og:locale': 'tr_TR',
        'og:site_name': 'Ankaplastik',
      },
      twitter: {
        'twitter:card': 'summary_large_image',
        'twitter:title': 'Tampon Baskı Hizmetleri | Ankaplastik',
        'twitter:description': "Bursa'da profesyonel tampon baskı hizmetleri. Otomotiv parçaları için hassas baskı.",
        'twitter:image': 'https://ankaplastik.com/og-tampon-baski.jpg',
      },
      schemaId: 'tampon-baski',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Tampon Baskı Hizmetleri',
        alternateName: 'Pad Printing',
        provider: {
          '@type': 'LocalBusiness',
          '@id': 'https://ankaplastik.com/#organization',
          name: 'Ankaplastik',
        },
        serviceType: 'Endüstriyel Baskı',
        areaServed: {
          '@type': 'Country',
          name: 'Türkiye',
        },
        description:
          '3D yüzeylere mükemmel uyum sağlayan tampon baskı (pad printing) hizmetleri. Otomotiv trim parçaları, gösterge panelleri, ISOFIX güvenlik parçaları için hassas baskı çözümleri.',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'TRY',
          },
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Tampon Baskı Uygulamaları',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Otomotiv İç Trim Parçaları Baskısı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Gösterge Paneli Baskısı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'ISOFIX Güvenlik Parçaları Baskısı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Çok Renkli Baskı' } },
          ],
        },
      },
    },
    hero: {
      titleLead: 'Tampon Baskı',
      titleTail: 'Hizmetleri',
      lead: '3D yüzeylere mükemmel uyum sağlayan tampon baskı teknolojimizle, en karmaşık geometrilere sahip parçalarda bile hassas ve kalıcı baskı çözümleri sunuyoruz.',
    },
    intro: {
      title: 'Dolaylı Gravür Baskı Teknolojisi',
      paragraphs: [
        'Tampon baskı (pad printing), silikon bir tampon aracılığıyla mürekkebin metal klişeden alınarak ürün yüzeyine aktarıldığı dolaylı bir baskı yöntemidir.',
        'Bu teknik sayesinde düz olmayan, eğimli, çukur veya bombeli yüzeylere bile yüksek kalitede baskı yapılabilir. Otomotiv, elektronik ve medikal sektörlerinde yaygın olarak tercih edilmektedir.',
      ],
    },
    advantages: [
      { title: '3D Yüzey Uyumu', desc: 'Karmaşık geometrilere ve eğimli yüzeylere mükemmel uyum' },
      { title: 'Çok Renkli Baskı', desc: 'Tek seferde birden fazla renk ile yüksek hassasiyetli baskı' },
      { title: 'Mikron Hassasiyeti', desc: 'En ince detayları bile yakalayan hassas baskı teknolojisi' },
      { title: 'Dayanıklı Mürekkepler', desc: 'UV, kimyasal ve aşınmaya karşı yüksek direnç' },
      { title: 'Yüksek Üretim Hızı', desc: 'Seri üretim için optimize edilmiş verimli prosesler' },
      { title: 'Çeşitli Malzemeler', desc: 'Plastik, metal, cam ve daha fazlasına baskı imkanı' },
    ],
    applications: [
      'Otomotiv iç trim parçaları',
      'Gösterge paneli bileşenleri',
      'Güvenlik parçaları (ISOFIX, klipsler)',
      'Elektronik cihaz kaplamaları',
      'Medikal ekipman işaretlemeleri',
      'Endüstriyel parça markalama',
    ],
    stats: [
      { label: 'Deneyim', value: '25+ yıl' },
      { label: 'Hata Oranı', value: '%0.01' },
    ],
    contact: {
      description: 'Tampon baskı ihtiyaçlarınız için bizimle iletişime geçin.',
      subjectPrefix: 'Tampon Baskı',
    },
    related: [
      {
        service: 'screen',
        name: 'Serigrafi Baskı',
        desc: 'Geniş yüzeylerde yüksek kaliteli elek baskı (screen printing) hizmetleri sunuyoruz.',
        features: ['Büyük alan baskı kapasitesi', 'Yüksek mürekkep kalınlığı'],
      },
      {
        service: 'assembly',
        name: 'Montaj & Trim',
        desc: 'Otomotiv trim parçaları ve güvenlik bileşenlerinin hassas montajını gerçekleştiriyoruz.',
        features: ['Hassas parça montajı', 'ISOFIX güvenlik parçaları'],
      },
    ],
  },

  en: {
    seo: {
      title: 'Pad Printing Services | Industrial Pad Printing - Ankaplastik Turkey',
      description:
        'Professional pad printing services for automotive industry. 3D surface printing technology, multi-color capability, micron precision. 25 years experience, ISO 9001:2015 certified.',
      og: {
        'og:title': 'Pad Printing Services | Ankaplastik Turkey',
        'og:description':
          'Industrial pad printing solutions for automotive tier-2 supply chain. Precision printing on complex 3D surfaces.',
        'og:type': 'website',
        'og:url': 'https://ankaplastik.com/en/pad-printing',
        'og:locale': 'en_US',
        'og:site_name': 'Ankaplastik',
      },
    },
    hero: {
      titleLead: 'Pad Printing',
      titleTail: 'Services',
      lead: 'Our advanced pad printing technology delivers flawless results on complex 3D surfaces, providing precise and durable printing solutions for the most challenging geometries.',
    },
    intro: {
      title: 'Indirect Gravure Printing Technology',
      paragraphs: [
        'Pad printing is an indirect printing method where ink is transferred from a metal cliché to the product surface via a silicone pad.',
        "This technique enables high-quality printing on non-flat, curved, concave, or convex surfaces. It's widely used in automotive, electronics, and medical industries.",
      ],
    },
    advantages: [
      { title: '3D Surface Compatibility', desc: 'Perfect adaptation to complex geometries and curved surfaces' },
      { title: 'Multi-Color Printing', desc: 'High-precision printing with multiple colors in a single pass' },
      { title: 'Micron Precision', desc: 'Precision printing technology capturing the finest details' },
      { title: 'Durable Inks', desc: 'High resistance to UV, chemicals, and abrasion' },
      { title: 'High Production Speed', desc: 'Efficient processes optimized for mass production' },
      { title: 'Material Versatility', desc: 'Print on plastic, metal, glass, and more' },
    ],
    applications: [
      'Automotive interior trim parts',
      'Dashboard components',
      'Safety parts (ISOFIX, clips)',
      'Electronic device housings',
      'Medical equipment markings',
      'Industrial part labeling',
    ],
    stats: [
      { label: 'Experience', value: '25+ years' },
      { label: 'Defect Rate', value: '0.01%' },
    ],
    contact: {
      description: 'Contact us for your pad printing requirements.',
      subjectPrefix: 'Pad Printing',
    },
    related: [
      {
        service: 'screen',
        name: 'Screen Printing',
        desc: 'High-quality screen printing services for large surfaces.',
        features: ['Large area coverage', 'Superior ink deposit'],
      },
      {
        service: 'assembly',
        name: 'Assembly & Trim',
        desc: 'Precision assembly of automotive trim parts and safety components.',
        features: ['Precision component assembly', 'ISOFIX safety parts'],
      },
    ],
  },

  de: {
    seo: {
      title: 'Tampondruck-Service | Industrieller Tampondruck - Ankaplastik Türkei',
      description:
        'Professioneller Tampondruck für die Automobilindustrie. 3D-Oberflächendruck, Mehrfarbenfähigkeit, Mikrometerpräzision. 25 Jahre Erfahrung, ISO 9001:2015 zertifiziert.',
    },
    hero: {
      titleLead: 'Tampondruck',
      titleTail: 'Services',
      lead: 'Unsere fortschrittliche Tampondrucktechnologie liefert makellose Ergebnisse auf komplexen 3D-Oberflächen und bietet präzise und langlebige Drucklösungen für anspruchsvollste Geometrien.',
    },
    intro: {
      title: 'Indirekte Tiefdrucktechnologie',
      paragraphs: [
        'Der Tampondruck ist ein indirektes Druckverfahren, bei dem Farbe von einem Metallklischee über einen Silikontampon auf die Produktoberfläche übertragen wird.',
        'Diese Technik ermöglicht hochwertigen Druck auf unebenen, gekrümmten, konkaven oder konvexen Oberflächen. Sie wird häufig in der Automobil-, Elektronik- und Medizinindustrie eingesetzt.',
      ],
    },
    advantages: [
      { title: '3D-Oberflächenkompatibilität', desc: 'Perfekte Anpassung an komplexe Geometrien und gekrümmte Oberflächen' },
      { title: 'Mehrfarbendruck', desc: 'Hochpräziser Druck mit mehreren Farben in einem Durchgang' },
      { title: 'Mikrometerpräzision', desc: 'Präzisionsdrucktechnologie, die feinste Details erfasst' },
      { title: 'Haltbare Farben', desc: 'Hohe Beständigkeit gegen UV, Chemikalien und Abrieb' },
      { title: 'Hohe Produktionsgeschwindigkeit', desc: 'Effiziente Prozesse optimiert für die Massenproduktion' },
      { title: 'Materialvielseitigkeit', desc: 'Druck auf Kunststoff, Metall, Glas und mehr' },
    ],
    applications: [
      'Fahrzeuginnenraum-Trimteile',
      'Armaturenbrettkomponenten',
      'Sicherheitsteile (ISOFIX, Clips)',
      'Gehäuse für elektronische Geräte',
      'Markierungen für medizinische Geräte',
      'Industrielle Teilekennzeichnung',
    ],
    stats: [
      { label: 'Erfahrung', value: '25+ Jahre' },
      { label: 'Fehlerquote', value: '0.01%' },
    ],
    contact: {
      description: 'Kontaktieren Sie uns für Ihre Tampondruck-Anforderungen.',
      subjectPrefix: 'Tampondruck',
    },
    related: [
      {
        service: 'screen',
        name: 'Siebdruck',
        desc: 'Hochwertiger Siebdruck für große Oberflächen.',
        features: ['Großflächenabdeckung', 'Überlegener Farbauftrag'],
      },
      {
        service: 'assembly',
        name: 'Montage & Trimteile',
        desc: 'Präzisionsmontage von Fahrzeug-Trimteilen und Sicherheitskomponenten.',
        features: ['Präzisionsmontage von Komponenten', 'ISOFIX-Sicherheitsteile'],
      },
    ],
  },
}

export default pad
